import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../lib/api';
import { getDashboardPath } from '../lib/navigation';
import Header from '../components/Header';

const ReportsPage = React.memo(() => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('dashboard');
    const [dashboardData, setDashboardData] = useState(null);
    const [investmentData, setInvestmentData] = useState(null);
    const [pitchEventData, setPitchEventData] = useState(null);
    const [userActivityData, setUserActivityData] = useState(null);

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const dashboardPath = getDashboardPath(tenantId, user.role);

    const COLORS = useMemo(() => ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'], []);

    const loadAllReports = useCallback(async () => {
        setLoading(true);
        try {
            const [dashboard, investments, pitchEvents, userActivity] = await Promise.all([
                api.get('/reports/dashboard').catch(err => {
                    console.error('Error loading dashboard report:', err);
                    return { data: null };
                }),
                api.get('/reports/investments').catch(err => {
                    console.error('Error loading investment report:', err);
                    return { data: null };
                }),
                api.get('/reports/pitch-events').catch(err => {
                    console.error('Error loading pitch event report:', err);
                    return { data: null };
                }),
                api.get('/reports/user-activity?days=30').catch(err => {
                    console.error('Error loading user activity report:', err);
                    return { data: null };
                })
            ]);

            setDashboardData(dashboard.data);
            setInvestmentData(investments.data);
            setPitchEventData(pitchEvents.data);
            setUserActivityData(userActivity.data);
        } catch (error) {
            console.error('Error loading reports:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAllReports();
    }, [loadAllReports]);

    // Define tabs before any conditional returns (React hooks rule)
    const tabs = useMemo(() => [
        { id: 'dashboard', label: 'Dashboard' },
        { id: 'investments', label: 'Investments' },
        { id: 'pitch-events', label: 'Pitch Events' },
        { id: 'user-activity', label: 'User Activity' }
    ], []);

    const renderDashboardTab = () => (
        <div className="space-y-6">
            <h2 className="text-2xl font-semibold text-gray-900">Dashboard Overview</h2>

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <KPICard
                    title="Total Users"
                    value={dashboardData?.totalUsers || 0}
                    icon="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    color="blue"
                />
                <KPICard
                    title="Active Startups"
                    value={dashboardData?.activeStartups || 0}
                    icon="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    color="green"
                />
                <KPICard
                    title="Total Investments"
                    value={dashboardData?.totalInvestments || 0}
                    icon="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    color="purple"
                />
                <KPICard
                    title="Total Invested"
                    value={formatCurrency(dashboardData?.totalInvestedAmount || 0)}
                    icon="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    color="yellow"
                />
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Pitch Events</h3>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Total Events</span>
                            <span className="font-semibold">{dashboardData?.totalPitchEvents || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Upcoming</span>
                            <span className="font-semibold text-blue-600">{dashboardData?.upcomingEvents || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Completed</span>
                            <span className="font-semibold text-green-600">{dashboardData?.completedEvents || 0}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Investor Network</h3>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Investor Networks</span>
                            <span className="font-semibold">{dashboardData?.investorNetworks || 0}</span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-gray-600">Active Investors</span>
                            <span className="font-semibold">{dashboardData?.activeInvestors || 0}</span>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Files</h3>
                    <div className="space-y-2">
                        <div className="flex justify-between">
                            <span className="text-gray-600">Total Files</span>
                            <span className="font-semibold">{dashboardData?.totalFiles || 0}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderInvestmentsTab = () => (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-900">Investment Analytics</h2>
                <div className="text-sm text-gray-500">
                    Total Invested: <span className="font-semibold text-gray-900">{formatCurrency(investmentData?.totalAmount || 0)}</span>
                    <span className="mx-2">•</span>
                    {investmentData?.totalInvestments || 0} Investments
                </div>
            </div>

            {/* Investment by Stage Chart */}
            {investmentData?.investmentsByStage && Object.keys(investmentData.investmentsByStage).length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Investments by Stage</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={Object.entries(investmentData.investmentsByStage).map(([key, value]) => ({
                            stage: key,
                            count: value?.count || 0,
                            amount: value?.totalAmount || 0
                        }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="stage" />
                            <YAxis yAxisId="left" />
                            <YAxis yAxisId="right" orientation="right" />
                            <Tooltip />
                            <Legend />
                            <Bar yAxisId="left" dataKey="count" fill="#3B82F6" name="Count" />
                            <Bar yAxisId="right" dataKey="amount" fill="#10B981" name="Amount (INR)" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Investments by Stage</h3>
                    <div className="text-center py-8 text-gray-500">No investment data available by stage</div>
                </div>
            )}

            {/* Top Investors */}
            {investmentData?.topInvestors && investmentData.topInvestors.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Investors by Amount</h3>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={investmentData.topInvestors.map(inv => ({
                            name: inv.investorName,
                            amount: inv.totalAmount,
                            count: inv.investmentCount
                        }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                            <YAxis />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Bar dataKey="amount" fill="#8B5CF6" name="Total Invested" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Investors by Amount</h3>
                    <div className="text-center py-8 text-gray-500">No investor data available</div>
                </div>
            )}

            {/* Top Startups */}
            {investmentData?.topStartups && investmentData.topStartups.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Funded Startups</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Startup</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Investments</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Raised</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {investmentData.topStartups.map((startup, idx) => (
                                    <tr key={idx}>
                                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{startup.startupName}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">{startup.investmentCount}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-gray-900">{formatCurrency(startup.totalAmount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Funded Startups</h3>
                    <div className="text-center py-8 text-gray-500">No startup investment data available</div>
                </div>
            )}
        </div>
    );

    const renderPitchEventsTab = () => (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-900">Pitch Event Reports</h2>
                <div className="text-sm text-gray-500">
                    Total Events: <span className="font-semibold text-gray-900">{pitchEventData?.totalEvents || 0}</span>
                </div>
            </div>

            {/* Events by Status */}
            {pitchEventData?.byStatus && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Events by Status</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={Object.entries(pitchEventData.byStatus).map(([name, value]) => ({ name, value }))}
                                    cx="50%"
                                    cy="50%"
                                    labelLine={false}
                                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    outerRadius={80}
                                    fill="#8884d8"
                                    dataKey="value"
                                >
                                    {Object.entries(pitchEventData.byStatus).map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Participation Stats</h3>
                        <div className="space-y-4">
                            <div>
                                <div className="flex justify-between mb-2">
                                    <span className="text-gray-600">Total Participants</span>
                                    <span className="font-semibold">{pitchEventData?.totalParticipants || 0}</span>
                                </div>
                            </div>
                            <div>
                                <div className="flex justify-between mb-2">
                                    <span className="text-gray-600">Avg Participants/Event</span>
                                    <span className="font-semibold">{(pitchEventData?.avgParticipantsPerEvent || 0).toFixed(1)}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Events by Type */}
            {pitchEventData?.byType && Object.keys(pitchEventData.byType).length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Events by Type</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={Object.entries(pitchEventData.byType).map(([name, value]) => ({ name, value }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#06B6D4" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Upcoming Events */}
            {pitchEventData?.upcomingEvents && pitchEventData.upcomingEvents.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Upcoming Events</h3>
                    <div className="space-y-3">
                        {pitchEventData.upcomingEvents.map(event => (
                            <div key={event.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div>
                                    <p className="font-medium text-gray-900">{event.eventName}</p>
                                    <p className="text-sm text-gray-500">
                                        {new Date(event.eventDate).toLocaleDateString()}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="text-sm font-medium text-gray-900">{event.participantCount} Participants</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    const renderUserActivityTab = () => (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-semibold text-gray-900">User Activity (Last {userActivityData?.periodDays || 30} Days)</h2>
            </div>

            {/* Activity Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <KPICard
                    title="Total Access Attempts"
                    value={userActivityData?.totalAccessAttempts || 0}
                    icon="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    color="blue"
                />
                <KPICard
                    title="Successful Access"
                    value={userActivityData?.successfulAccess || 0}
                    icon="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    color="green"
                />
                <KPICard
                    title="Denied Access"
                    value={userActivityData?.deniedAccess || 0}
                    icon="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    color="red"
                />
                <KPICard
                    title="Success Rate"
                    value={`${userActivityData?.totalAccessAttempts ?
                        ((userActivityData.successfulAccess / userActivityData.totalAccessAttempts) * 100).toFixed(1) : 0}%`}
                    icon="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                    color="purple"
                />
            </div>

            {/* Access by Type */}
            {userActivityData?.byAccessType && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Access by Type</h3>
                    <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={Object.entries(userActivityData.byAccessType).map(([name, value]) => ({ name, value }))}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="value" fill="#F59E0B" />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            )}

            {/* Most Active Users */}
            {userActivityData?.mostActiveUsers && userActivityData.mostActiveUsers.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Active Users</h3>
                    <div className="space-y-3">
                        {userActivityData.mostActiveUsers.map((user, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                        <span className="text-blue-600 font-semibold text-sm">{idx + 1}</span>
                                    </div>
                                    <span className="font-medium text-gray-900">{user.userName}</span>
                                </div>
                                <span className="text-sm font-semibold text-gray-900">{user.activityCount} activities</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Most Accessed Files */}
            {userActivityData?.mostAccessedFiles && userActivityData.mostAccessedFiles.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Accessed Files</h3>
                    <div className="space-y-3">
                        {userActivityData.mostAccessedFiles.map((file, idx) => (
                            <div key={idx} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                                <div className="flex items-center space-x-3">
                                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                        <span className="text-green-600 font-semibold text-sm">{idx + 1}</span>
                                    </div>
                                    <span className="font-medium text-gray-900 truncate max-w-md">{file.fileName}</span>
                                </div>
                                <span className="text-sm font-semibold text-gray-900">{file.accessCount} times</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Header />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" style={{ paddingTop: '5.5rem' }}>
                    <div className="mb-8">
                        <div className="flex items-center space-x-4 mb-4">
                            <button
                                onClick={() => navigate(dashboardPath)}
                                className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 transition-colors text-gray-600 hover:text-gray-900"
                                title="Back to Dashboard"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center justify-center py-16">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                            <p className="mt-4 text-gray-600">Loading reports...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-20" style={{ paddingTop: '5.5rem' }}>
                {/* Page Header with Back Button */}
                <div className="mb-8">
                    <div className="flex items-center space-x-4 mb-4">
                        <button
                            onClick={() => navigate(dashboardPath)}
                            className="flex items-center justify-center w-10 h-10 rounded-lg bg-white border border-gray-300 hover:bg-gray-50 transition-colors text-gray-600 hover:text-gray-900"
                            title="Back to Dashboard"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
                    <div className="border-b border-gray-200">
                        <nav className="flex -mb-px">
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`px-6 py-4 text-sm font-medium border-b-2 transition ${
                                        activeTab === tab.id
                                            ? 'border-blue-600 text-blue-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>

                {/* Content */}
                <div>
                    {activeTab === 'dashboard' && renderDashboardTab()}
                    {activeTab === 'investments' && renderInvestmentsTab()}
                    {activeTab === 'pitch-events' && renderPitchEventsTab()}
                    {activeTab === 'user-activity' && renderUserActivityTab()}
                </div>
            </div>
        </div>
    );
});

ReportsPage.displayName = 'ReportsPage';

const KPICard = React.memo(({ title, value, icon, color }) => {
    const colorClasses = {
        blue: 'bg-blue-100 text-blue-600',
        green: 'bg-green-100 text-green-600',
        purple: 'bg-purple-100 text-purple-600',
        yellow: 'bg-yellow-100 text-yellow-600',
        red: 'bg-red-100 text-red-600'
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center">
                <div className="flex-shrink-0">
                    <div className={`w-12 h-12 ${colorClasses[color] || colorClasses.blue} rounded-lg flex items-center justify-center`}>
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={icon} />
                        </svg>
                    </div>
                </div>
                <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">{title}</p>
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                </div>
            </div>
        </div>
    );
});

KPICard.displayName = 'KPICard';

const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(amount);
};

export default ReportsPage;
